import { API_ENDPOINTS } from '@/api/endpoints';
import {
  Subproject,
  SubprojectBuildingDetails,
} from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';

import { getSelectedOrganization } from '../organization/orgUtils';

interface CreateSubprojectParams {
  projectId: string;
  name: string;
  userSubprojId: string;
  address: string;
  description: string;
  sector: string;
  sponsoringDepts: string[];
  overseeingDepts: string[];
  usingDepts: string[];
  programManagers: string[];
  projectManagers: string[];
  budgetOfficers: string[];
  organizationId?: string;
}

// Create a new subproject
export async function createSubproject(
  params: CreateSubprojectParams,
): Promise<Subproject> {
  const selectedOrg = getSelectedOrganization();

  if (!selectedOrg && !params.organizationId) {
    throw new Error('No organization selected or provided');
  }

  const organizationId = params.organizationId ?? selectedOrg?.id;

  if (!organizationId) {
    throw new Error('Unable to determine organization ID');
  }

  const subprojectData = {
    projectId: params.projectId,
    name: params.name,
    userSubprojId: params.userSubprojId,
    address: params.address,
    description: params.description,
    sector: params.sector,
    sponsoringDepts: params.sponsoringDepts,
    overseeingDepts: params.overseeingDepts,
    usingDepts: params.usingDepts,
    programManagers: params.programManagers,
    projectManagers: params.projectManagers,
    budgetOfficers: params.budgetOfficers,
    organizationId,
  };

  try {
    try {
      const response = await apiFetch<Subproject>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.SUBPROJECTS.CREATE(
          organizationId,
          params.projectId,
        ),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(subprojectData),
        },
      );
      return response;
    } catch (error) {
      console.error('Error creating subproject:', error);
      throw new Error(
        `Failed to create subproject: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  } catch (error) {
    console.error('Failed to create subproject:', error);
    throw error;
  }
}

// Fetch a subproject's details
export async function fetchSubprojectDetails(
  subprojectId: string,
  organizationId?: string,
): Promise<SubprojectBuildingDetails> {
  const selectedOrg = getSelectedOrganization();

  if (!selectedOrg && !organizationId) {
    throw new Error('No organization selected or provided');
  }

  const orgId = organizationId ?? selectedOrg?.id;

  if (!orgId) {
    throw new Error('Unable to determine organization ID');
  }

  return apiFetch<SubprojectBuildingDetails>(
    API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(orgId, subprojectId),
  );
}

// Update a subproject
export async function updateSubproject<T extends Subproject>(
  subproject: T,
): Promise<T> {
  const selectedOrg = getSelectedOrganization();

  if (!selectedOrg) {
    throw new Error('No organization selected');
  }

  // Use the standard subproject detail endpoint
  const endpoint = API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(
    selectedOrg.id,
    subproject.id,
  );

  // Transform frontend data to match backend expectations
  const { projectId: project, ...restSubproject } = subproject;

  const backendData = {
    ...restSubproject,
    project,
  };

  return apiFetch<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(backendData),
  });
}

export async function updateSubprojectDetails(
  subproject: SubprojectBuildingDetails,
): Promise<SubprojectBuildingDetails> {
  const selectedOrg = getSelectedOrganization();

  if (!selectedOrg) {
    throw new Error('No organization selected');
  }

  // Use the standard subproject detail endpoint
  const endpoint = API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(
    selectedOrg.id,
    subproject.id,
  );

  const backendData = {
    ...subproject,
  };

  return apiFetch<SubprojectBuildingDetails>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(backendData),
  });
}

// Fetch subprojects for a project
/**
 * Fetch subprojects for a project, scoped to an organization.
 * @param projectId ID of the project
 * @param organizationId Optional organization ID to use instead of the selected one
 */
export async function fetchSubprojects(
  projectId: string,
  organizationId?: string,
): Promise<SubprojectBuildingDetails[]> {
  const selectedOrg = getSelectedOrganization();

  if (!selectedOrg && !organizationId) {
    throw new Error('No organization selected or provided');
  }

  const orgId = organizationId ?? selectedOrg?.id;
  if (!orgId) {
    throw new Error('Unable to determine organization ID');
  }

  return apiFetch<SubprojectBuildingDetails[]>(
    API_ENDPOINTS.ORGANIZATIONS.PROJECTS.SUBPROJECTS.LIST(orgId, projectId),
  );
}
